import { motion, overrideVariants } from '@/lib/motion';
import { Clock, Home, Wallet2, Coins, Calculator, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { AnimatedNumber } from '../ui/AnimatedNumber';
import { format, isValid } from 'date-fns';
import * as Popover from '@radix-ui/react-popover';
import { useEffect, useRef } from 'react';
// Import the CostBreakdownBox component
import { CostBreakdownBox } from './MonthlyDonutChart';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
}

function Tooltip({ children, content }: TooltipProps) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button className="inline-flex items-center gap-1 hover:text-gray-900">
          {children}
          <Info className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-72 p-3 bg-white rounded-lg shadow-lg border border-gray-200 text-sm text-gray-600"
          sideOffset={1}>
          {content}
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function calculatePropertyTax(price: number): number {
  if (price <= 300000) return price * 0.0125 / 12;     // 1.25% for starter homes
  if (price <= 500000) return price * 0.0110 / 12;     // 1.10% for mid-range homes
  if (price <= 750000) return price * 0.0100 / 12;     // 1.00% for upper mid-range
  if (price <= 1000000) return price * 0.0085 / 12;    // 0.85% for entry luxury
  if (price <= 1500000) return price * 0.0075 / 12;    // 0.75% for luxury homes
  if (price <= 2000000) return price * 0.0065 / 12;    // 0.65% for high-end luxury
  if (price <= 3000000) return price * 0.0060 / 12;    // 0.60% for premium luxury
  return price * 0.0050 / 12;                          // 0.50% for ultra-luxury
}

function calculateHomeInsurance(price: number): number {
  if (price <= 300000) return price * 0.0035 / 12;    // 0.35% for starter homes
  if (price <= 500000) return price * 0.0032 / 12;    // 0.32% for mid-range homes
  if (price <= 750000) return price * 0.0030 / 12;    // 0.30% for upper mid-range
  if (price <= 1000000) return price * 0.0028 / 12;   // 0.28% for entry luxury
  if (price <= 1500000) return price * 0.0025 / 12;   // 0.25% for luxury homes
  if (price <= 2000000) return price * 0.0023 / 12;   // 0.23% for high-end luxury
  if (price <= 3000000) return price * 0.0021 / 12;   // 0.21% for premium luxury
  return price * 0.0020 / 12;                         // 0.20% for ultra-luxury
}

function calculateMaintenance(price: number): number {
  if (price <= 300000) return price * 0.0075 / 12;     // 0.75% for starter homes (often older)
  if (price <= 500000) return price * 0.0065 / 12;     // 0.65% for mid-range homes
  if (price <= 750000) return price * 0.0050 / 12;     // 0.50% for upper mid-range
  if (price <= 1000000) return price * 0.0040 / 12;    // 0.40% for entry luxury
  if (price <= 1500000) return price * 0.0035 / 12;    // 0.35% for luxury homes
  if (price <= 2000000) return price * 0.0030 / 12;    // 0.30% for high-end luxury
  if (price <= 3000000) return price * 0.0025 / 12;    // 0.25% for premium luxury
  return price * 0.0020 / 12;                          // 0.20% for ultra-luxury
}

function getRatioStatus(ratio: number) {
  if (ratio < 38) {
    return {
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      icon: CheckCircle2,
      label: 'Good'
    };
  } else if (ratio < 45) {
    return {
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      icon: AlertTriangle,
      label: 'Stretched'
    };
  } else {
    return {
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      icon: AlertTriangle,
      label: 'High'
    };
  }
}

interface KPIHighlightsProps {
  price: number;
  purchaseDate: Date | null;
  monthlyPayment: number;
  interestRate: number;
  amortizationYears: number;
  downPayment: number;
  initialDownPayment: number;
  additionalSavings: number;
  mortgageAmount: number;
  totalInterestPaid: number;
  monthlyIncome: number;
  savingPeriodMonths: number;
}

export function KPIHighlights({
  price,
  purchaseDate,
  monthlyPayment,
  interestRate,
  amortizationYears,
  downPayment,
  initialDownPayment,
  additionalSavings,
  mortgageAmount,
  totalInterestPaid,
  monthlyIncome,
  savingPeriodMonths
}: KPIHighlightsProps) {
  // Refs for animation frames
  const animationFrameRef = useRef<number>();
  const totalMonthlyCostsRef = useRef<HTMLDivElement>(null);

  // Clean up animation frames on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const propertyTax = calculatePropertyTax(price);
  const homeInsurance = calculateHomeInsurance(price);
  const maintenance = calculateMaintenance(price);
  const utilities = Math.min(500, 200 + Math.floor(price / 500000) * 50);
  const mortgageInsurance = downPayment / price < 0.2 ? mortgageAmount * 0.005 / 12 : 0;

  const totalMonthlyCost = monthlyPayment + propertyTax + homeInsurance + maintenance + utilities + mortgageInsurance;
  const housingCostRatio = Math.round((totalMonthlyCost / monthlyIncome) * 100);
  
  // Ensure we have a valid Date object by properly parsing the input
  const validPurchaseDate = purchaseDate ? 
    (purchaseDate instanceof Date ? purchaseDate : new Date(purchaseDate)) : 
    new Date();

  // Validate the date and fall back to current date if invalid
  const finalDate = isValid(validPurchaseDate) ? validPurchaseDate : new Date();
  
  const monthsUntilPurchase = savingPeriodMonths;
  const yearsUntilPurchase = Math.floor(monthsUntilPurchase / 12);
  const remainingMonths = monthsUntilPurchase % 12;

  const getTimeDescription = (): string => {
    if (monthsUntilPurchase <= 0) return 'Ready to purchase';
    if (yearsUntilPurchase > 0) {
      return remainingMonths > 0 
        ? `${yearsUntilPurchase} year${yearsUntilPurchase > 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`
        : `${yearsUntilPurchase} year${yearsUntilPurchase > 1 ? 's' : ''}`;
    }
    return `${monthsUntilPurchase} month${monthsUntilPurchase > 1 ? 's' : ''}`;
  };

  const ratioStatus = getRatioStatus(housingCostRatio);

  // Using the simplified variants from our centralized motion file
  // No need to define local variants

  // Unified KPI card component to avoid code duplication and ensure consistency
  const KpiCard = ({ icon: Icon, title, titleTooltip, value, details, percentage }: {
    icon: any,
    title: string,
    titleTooltip: React.ReactNode,
    value: number,
    details: React.ReactNode,
    percentage?: number
  }) => (
    <motion.div
      variants={overrideVariants.itemVariants}
      className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 mb-0 sm:mb-1">
        <Icon className="w-5 h-5 text-primary flex-shrink-0" />
        <Tooltip content={titleTooltip}>
          <h4 className="text-sm font-medium text-gray-600">{title}</h4>
        </Tooltip>
      </div>
      <div className="text-2xl font-bold text-gray-900 sm:mt-0">
        <AnimatedNumber value={value} formatter={formatCurrency} />
        {percentage !== undefined && (
          <span className="text-lg font-normal text-gray-600 ml-1">
            ({percentage.toFixed(1)}%)
          </span>
        )}
      </div>
      <div className="mt-0 sm:mt-0.5 space-y-0.5 text-sm">
        {details}
      </div>
    </motion.div>
  );

  // Unified Total Monthly Costs card
  const TotalMonthlyCostsCard = () => (
    <motion.div
      variants={overrideVariants.itemVariants}
      className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm"
      ref={totalMonthlyCostsRef}
      id="total-monthly-costs">
      <div className="flex items-center gap-2 mb-0 sm:mb-1">
        <Calculator className="w-5 h-5 text-primary flex-shrink-0" />
        <Tooltip content={
          <div className="space-y-2">
            <div>
              <strong>Included in Debt Service Ratio:</strong> Your maximum affordable home price is calculated based on your debt service ratio, which includes your mortgage payment (principal and interest), property tax, insurance (including mortgage insurance if applicable), and utilities.
            </div>
            <div>
              <strong>Property Tax:</strong> Ranges from 1.25% to 0.5% annually, with lower rates for higher-value homes.
            </div>
            <div>
              <strong>Home Insurance:</strong> Annual rates range from 0.35% to 0.2%, decreasing for higher-value homes.
            </div>
            <div>
              <strong>Mortgage Insurance:</strong> Required if down payment is less than 20%, costs 0.5% of loan amount annually.
            </div>
            <div>
              <strong>Maintenance:</strong> Annual costs range from 1% to 0.2% of home value. This is not included in the debt service ratio constraint as it's variable and discretionary, but should be budgeted for separately.
            </div>
            <div>
              <strong>Utilities:</strong> Starting at $200 monthly, increasing with home size up to $500 maximum.
            </div>
            <div>
              <strong>Housing Cost Ratio:</strong> The percentage of monthly income that goes toward total housing costs, including maintenance. Unlike the debt service ratio slider (which excludes maintenance when calculating affordability), this ratio includes estimated maintenance costs. Under 38% is good, 38-44% is stretched, and 45% or higher is high.
            </div>
          </div>
        }>
          <h3 className="text-sm font-medium text-gray-600">Estimated Total Monthly Costs</h3>
        </Tooltip>
      </div>
      <div className="text-2xl font-bold text-primary sm:mt-0 mb-2 sm:mb-1" data-testid="kpi-total-monthly-cost">
        <AnimatedNumber value={totalMonthlyCost} formatter={formatCurrency} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm mt-0 sm:mt-1">
        <div className="leading-tight">
          <div className="text-gray-600 mb-0.5">Monthly Payment</div>
          <div className="font-medium text-base -mt-0.5" data-testid="kpi-mortgage-payment">{formatCurrency(monthlyPayment)}</div>
        </div>
        <div className="leading-tight">
          <div className="text-gray-600 mb-0.5">Property Tax</div>
          <div className="font-medium text-base -mt-0.5" data-testid="kpi-property-tax">{formatCurrency(propertyTax)}</div>
        </div>
        <div className="leading-tight">
          <div className="text-gray-600 mb-0.5">Insurance</div>
          <div className="font-medium text-base -mt-0.5" data-testid="kpi-insurance">
            {formatCurrency(homeInsurance)}
            {mortgageInsurance > 0 && (
              <span className="text-gray-600 ml-1">+{formatCurrency(mortgageInsurance)} MI</span>
            )}
          </div>
        </div>
        <div className="leading-tight">
          <div className="text-gray-600 mb-0.5">Maintenance</div>
          <div className="font-medium text-base -mt-0.5" data-testid="kpi-maintenance">{formatCurrency(maintenance)}</div>
        </div>
        <div className="leading-tight">
          <div className="text-gray-600 mb-0.5">Utilities</div>
          <div className="font-medium text-base -mt-0.5" data-testid="kpi-utilities">{formatCurrency(utilities)}</div>
        </div>
        <div className="leading-tight">
          <div className="text-gray-600 mb-0.5">Housing Cost Ratio</div>
          <div className="flex items-center gap-2">
            <div className="font-medium text-base" data-testid="kpi-cost-ratio">{housingCostRatio}%</div>
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${ratioStatus.bgColor} ${ratioStatus.color} border ${ratioStatus.borderColor}`}>
              <ratioStatus.icon className="w-3.5 h-3.5" />
              <span>{ratioStatus.label}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <motion.div
      variants={overrideVariants.containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-2 mt-2">
      
      {/* MOBILE VIEW */}
      <div className="block sm:hidden space-y-2">
        {/* 1. Mortgage */}
        <KpiCard
          icon={Home}
          title="Mortgage"
          titleTooltip="This represents your mortgage's starting principal balance and the total interest paid over the life of the loan - together forming your home's true long-term cost beyond the purchase price. Understanding these figures helps you see the full financial commitment of your mortgage. See the amortization table below for a detailed year-by-year analysis."
          value={mortgageAmount}
          details={
            <>
              <div className="text-gray-600">Total Interest: {formatCurrency(totalInterestPaid)}</div>
              <div className="text-gray-600">Total Cost: {formatCurrency(mortgageAmount + totalInterestPaid)}</div>
            </>
          }
        />
        
        {/* 2. Monthly Payment */}
        <KpiCard
          icon={Clock}
          title="Monthly Payment"
          titleTooltip="Your monthly mortgage payment based on the loan amount, interest rate, and term length. You can adjust the assumed mortgage term and interest rate by editing the calculator inputs. This is a fixed cost assuming a fixed-rate mortgage."
          value={monthlyPayment}
          details={
            <>
              <div className="text-gray-600">Mortgage Interest: {formatNumber(interestRate)}%</div>
              <div className="text-gray-600">Term: {amortizationYears} years</div>
            </>
          }
        />

        {/* 3. Purchase Price */}
        <KpiCard
          icon={Coins}
          title="Purchase Price"
          titleTooltip="The total purchase price of the home, including both your down payment and mortgage amount. This represents the full amount you're agreeing to pay for the property at closing."
          value={price}
          details={
            <>
              <div className="text-gray-600">
                Purchase Date: {format(finalDate, 'MMM d, yyyy')}
              </div>
              <div className="text-gray-600">
                Time: {getTimeDescription()}
              </div>
            </>
          }
        />

        {/* 4. Down Payment */}
        <KpiCard
          icon={Wallet2}
          title="Down Payment"
          titleTooltip="Your total down payment combines your current savings and additional savings until purchase. You can edit your assumed additional monthly savings in the inputs. There is no investment rate of return assumed on the down payment balance or the additional monthly savings. A 20% down payment helps avoid mortgage insurance and secure better rates."
          value={downPayment}
          percentage={(downPayment / price) * 100}
          details={
            <>
              <div className="text-gray-600">Current Savings: {formatCurrency(initialDownPayment)}</div>
              <div className="text-gray-600">Additional Needed: {formatCurrency(additionalSavings)}</div>
            </>
          }
        />

        {/* 5. Total Monthly Costs Chart */}
        <motion.div variants={overrideVariants.itemVariants}>
          <CostBreakdownBox
            totalCost={totalMonthlyCost}
            monthlyPayment={monthlyPayment}
            propertyTax={propertyTax}
            homeInsurance={homeInsurance}
            mortgageInsurance={mortgageInsurance}
            maintenance={maintenance}
            utilities={utilities}
          />
        </motion.div>

        {/* 6. Total Monthly Costs */}
        <TotalMonthlyCostsCard />
      </div>

      {/* DESKTOP VIEW - First row with four equal boxes */}
      <div className="hidden sm:grid sm:grid-cols-4 gap-2">
        {/* 1. Mortgage */}
        <KpiCard
          icon={Home}
          title="Mortgage"
          titleTooltip="This represents your mortgage's starting principal balance and the total interest paid over the life of the loan - together forming your home's true long-term cost beyond the purchase price. Understanding these figures helps you see the full financial commitment of your mortgage. See the amortization table below for a detailed year-by-year analysis."
          value={mortgageAmount}
          details={
            <>
              <div className="text-gray-600">Total Interest: {formatCurrency(totalInterestPaid)}</div>
              <div className="text-gray-600">Total Cost: {formatCurrency(mortgageAmount + totalInterestPaid)}</div>
            </>
          }
        />

        {/* 2. Monthly Payment */}
        <KpiCard
          icon={Clock}
          title="Monthly Payment"
          titleTooltip="Your monthly mortgage payment based on the loan amount, interest rate, and term length. You can adjust the assumed mortgage term and interest rate by editing the calculator inputs. This is a fixed cost assuming a fixed-rate mortgage."
          value={monthlyPayment}
          details={
            <>
              <div className="text-gray-600">Mortgage Interest: {formatNumber(interestRate)}%</div>
              <div className="text-gray-600">Term: {amortizationYears} years</div>
            </>
          }
        />

        {/* 3. Purchase Price */}
        <KpiCard
          icon={Coins}
          title="Purchase Price"
          titleTooltip="The total purchase price of the home, including both your down payment and mortgage amount. This represents the full amount you're agreeing to pay for the property at closing."
          value={price}
          details={
            <>
              <div className="text-gray-600">
                Purchase Date: {format(finalDate, 'MMM d, yyyy')}
              </div>
              <div className="text-gray-600">
                Time: {getTimeDescription()}
              </div>
            </>
          }
        />

        {/* 4. Down Payment */}
        <KpiCard
          icon={Wallet2}
          title="Down Payment"
          titleTooltip="Your total down payment combines your current savings and additional savings until purchase. You can edit your assumed additional monthly savings in the inputs. There is no investment rate of return assumed on the down payment balance or the additional monthly savings. A 20% down payment helps avoid mortgage insurance and secure better rates."
          value={downPayment}
          percentage={(downPayment / price) * 100}
          details={
            <>
              <div className="text-gray-600">Current Savings: {formatCurrency(initialDownPayment)}</div>
              <div className="text-gray-600">Additional Needed: {formatCurrency(additionalSavings)}</div>
            </>
          }
        />
      </div>

      {/* Second row - Total Monthly Costs and Chart */}
      <div className="hidden sm:grid sm:grid-cols-2 gap-2">
        {/* 5. Total Monthly Costs Chart */}
        <motion.div variants={overrideVariants.itemVariants}>
          <CostBreakdownBox
            totalCost={totalMonthlyCost}
            monthlyPayment={monthlyPayment}
            propertyTax={propertyTax}
            homeInsurance={homeInsurance}
            mortgageInsurance={mortgageInsurance}
            maintenance={maintenance}
            utilities={utilities}
          />
        </motion.div>

        {/* 6. Total Monthly Costs */}
        <TotalMonthlyCostsCard />
      </div>
    </motion.div>
  );
}